/**
 * College Service Utilities
 * Helper functions for data transformation and parsing
 */

import { College } from "../../data/colleges";
import type { CollegeFilters, CollegeRow } from "./types";

// ============================================
// Conversion Functions
// ============================================

/**
 * Convert IPEDS locale code to campus type
 * IPEDS locale codes: 11,12,13 = City, 21,22,23 = Suburban, 31,32,33 = Town, 41,42,43 = Rural
 */
export function localeToCampusType(
  locale: number | null,
): "Urban" | "Suburban" | "Rural" {
  if (!locale) return "Urban";
  if (locale >= 11 && locale <= 13) return "Urban";
  if (locale >= 21 && locale <= 23) return "Suburban";
  if (locale >= 31 && locale <= 43) return "Rural";
  return "Urban";
}

// Debug flag for size code logging
let sizeCodeDebugLogged = false;

/**
 * Convert IPEDS size code to size category
 * Database values: 6-10 = Small, 11-14 = Medium, 15-17 = Large
 */
export function sizeCodeToSize(
  sizeCode: number | null,
): "Small" | "Medium" | "Large" {
  if (!sizeCode) return "Medium";

  // Debug: Log the actual size code value (only for first call)
  if (!sizeCodeDebugLogged) {
    sizeCodeDebugLogged = true;
  }

  if (sizeCode <= 10) return "Small";
  if (sizeCode <= 14) return "Medium";
  return "Large";
}

// ============================================
// Parsing Functions
// ============================================

/**
 * Parse SAT scores from text fields
 */
export function parseSATScore(satText: string | null): number | null {
  if (!satText) return null;
  const num = parseInt(satText, 10);
  return isNaN(num) ? null : num;
}

/**
 * Parse majors from text (handling various formats)
 */
export function parseMajors(majorsText: string | null): string[] {
  if (!majorsText) return [];

  const trimmed = majorsText.trim();
  if (!trimmed) return [];

  // Try parsing as JSON array first (e.g., '["Business", "Management"]')
  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      return parsed.map((m) => String(m).trim()).filter((m) => m.length > 0);
    }
  } catch {
    // Not valid JSON, continue with other formats
  }

  // Handle JavaScript array string with single quotes
  if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
    let cleaned = trimmed.slice(1, -1).trim();
    cleaned = cleaned.replace(/'/g, '"');
    try {
      const parsed = JSON.parse("[" + cleaned + "]");
      if (Array.isArray(parsed)) {
        return parsed.map((m) => String(m).trim()).filter((m) => m.length > 0);
      }
    } catch {
      // Continue with manual parsing
    }
  }

  // Clean up brackets and quotes
  let cleaned = trimmed
    .replace(/^\[+|\]+$/g, "")
    .replace(/^\{+|\}+$/g, "")
    .replace(/^['"]+|['"]+$/g, "");
  if (cleaned.startsWith("{") && cleaned.endsWith("}")) {
    cleaned = cleaned.slice(1, -1).trim();
  }

  // Split by comma and clean up
  const items = cleaned
    .split(",")
    .map((item) => {
      let cleanedItem = item.trim();
      cleanedItem = cleanedItem
        .replace(/^\[+|\]+$/g, "")
        .replace(/^\{+|\}+$/g, "")
        .replace(/^['"]+|['"]+$/g, "")
        .replace(/['"]/g, "")
        .trim();
      return cleanedItem;
    })
    .filter((item) => item.length > 0 && !item.match(/^[\[\]{}'"]+$/));

  if (items.length > 0) {
    return items;
  }

  // Single string with no commas
  let singleItem = trimmed;
  while (
    (singleItem.startsWith('"') && singleItem.endsWith('"')) ||
    (singleItem.startsWith("'") && singleItem.endsWith("'")) ||
    (singleItem.startsWith("`") && singleItem.endsWith("`"))
  ) {
    singleItem = singleItem.slice(1, -1).trim();
  }

  return singleItem ? [singleItem] : [];
}

// ============================================
// Transform Functions
// ============================================

/**
 * Transform a flat college_recommender_data row to the frontend College type
 */
export function transformDatabaseToCollege(row: CollegeRow): College {
  const satVerbal = parseSATScore(row.filled_satvr50 || null);
  const satMath = parseSATScore(row.filled_satmt50 || null);
  const satCombined =
    satVerbal && satMath ? satVerbal + satMath : satVerbal || satMath || null;

  return {
    id: String(row.csc_unitid || row.ipeds_unitid || ""),
    name: row.csc_instnm || "",
    location: {
      city: row.csc_city || "",
      state: row.csc_stabbr || "",
    },
    tuition: {
      inState: row.csc_cinsoff || null,
      outOfState: row.csc_cinson || null,
    },
    sat: {
      min: satCombined,
      max: satCombined,
    },
    gpa: {
      min: row.estimated_gpa && row.estimated_gpa > 0 ? row.estimated_gpa : null,
      max: row.estimated_gpa && row.estimated_gpa > 0 ? row.estimated_gpa : null,
    },
    size: sizeCodeToSize(row.csc_ccsizset || row.ipeds_hd2022__instsize || null),
    campusType: localeToCampusType(row.csc_locale || null),
    acceptanceRate: row.filled_adm_rate || null,
    description: "",
    majors: parseMajors(row.csc_majors || null),
    logo: row.logo_url || "",
  };
}

// ============================================
// Client-side Filters
// ============================================

/**
 * Apply client-side filters that require complex logic
 */
export function applyClientSideFilters(
  colleges: College[],
  filters: CollegeFilters,
): College[] {
  let result = colleges;

  // Apply majors filter (case-insensitive)
  if (filters.majors && filters.majors.length > 0) {
    const filterMajorsLower = filters.majors.map((m) => m.toLowerCase().trim());
    result = result.filter((college) => {
      if (!college.majors || college.majors.length === 0) {
        return false;
      }
      const collegeMajorsLower = college.majors.map((m) =>
        m.toLowerCase().trim(),
      );
      return filterMajorsLower.some((filterMajor) =>
        collegeMajorsLower.some(
          (collegeMajor) =>
            collegeMajor.includes(filterMajor) ||
            filterMajor.includes(collegeMajor),
        ),
      );
    });
  }

  // Apply SAT range filter
  if (filters.satRange) {
    const [minSat, maxSat] = filters.satRange;
    result = result.filter((college) => {
      if (college.sat.min === null && college.sat.max === null) return true;
      const avgSat =
        college.sat.min !== null ? college.sat.min : college.sat.max;
      if (avgSat === null) return true;
      return avgSat >= minSat && avgSat <= maxSat;
    });
  }

  // Apply ACT range filter
  if (filters.actRange) {
    // ACT filtering: We don't have ACT data in the College type currently
    // The range is destructured but not used since there's no ACT field
    // This filter is a no-op placeholder for future ACT data support
    // result stays unchanged
  }

  // Apply GPA range filter
  if (filters.gpaRange) {
    const [minGpa, maxGpa] = filters.gpaRange;
    result = result.filter((college) => {
      if (college.gpa.min === null && college.gpa.max === null) return true;
      const avgGpa =
        college.gpa.min !== null ? college.gpa.min : college.gpa.max;
      if (avgGpa === null) return true;
      return avgGpa >= minGpa && avgGpa <= maxGpa;
    });
  }

  // Apply size filter
  if (filters.size && filters.size.length > 0) {
    result = result.filter((college) =>
      filters.size!.some((s) => s.toLowerCase() === college.size.toLowerCase()),
    );
  }

  // Apply campus type filter
  if (filters.campusType && filters.campusType.length > 0) {
    result = result.filter((college) =>
      filters.campusType!.some(
        (c) => c.toLowerCase() === college.campusType.toLowerCase(),
      ),
    );
  }

  // Apply search query filter
  if (filters.searchQuery) {
    const query = filters.searchQuery.toLowerCase().trim();
    result = result.filter(
      (college) =>
        college.name.toLowerCase().includes(query) ||
        college.location.city.toLowerCase().includes(query) ||
        college.location.state.toLowerCase().includes(query) ||
        college.majors.some((m) => m.toLowerCase().includes(query)),
    );
  }

  return result;
}
